import { signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { CvMapSignalStore } from './cv-map-signal-store';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { ApiService } from '../../../../core/api-service';
import { LocationListApiService } from '../../location-list/location-list-api.service';
import { FeedbackService } from '../../../../core/feedback.service';
import { convertToCoordinateType, CvMarkerAction, CvMarkerType, determineMarkerAction, MapMarkerData } from '../map-utils';
import { Coordinate } from '../../../../models/coordinate';
import { CoordinateSource } from '../../../../models/coordinate-history';
import { selectFocusedLocation, selectValidationHistory } from '../../location-list/state/location-list-selectors';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { LocationsRefreshActions, RowActions } from '../../location-list/state/location-list-actions';
import { selectCanContinueAfterValidation } from '../state/map-combined.selectors';

export function withCvActions<_>() {
  return signalStoreFeature(
      { state: type<CvMapSignalStore>() },
      withMethods((signalStore,
                   store = inject(Store),
                   apiService = inject(ApiService),
                   locationListApi = inject(LocationListApiService),
                   feedbackService = inject(FeedbackService)) => {
        /**
         * A marker position is rounded (unless it's an intermediate, draggable CUSTOM marker).
         * Gets the original coordinates from respective data source.
         * @param marker
         * @private
         */
        const getPreciseCoordinates = (marker: MapMarkerData):
            { coordinates: Coordinate, src: CoordinateSource } => {
          const location = store.selectSignal(selectFocusedLocation);
          const validationHistory = store.selectSignal(selectValidationHistory);

          switch (marker.type) {
            case CvMarkerType.HISTORY:
              const item = validationHistory()
                  ?.find(vh => String(vh.id) === marker.id);

              return { coordinates: item.coordinates, src: item.source };
            case CvMarkerType.GEOCODE:
              return { coordinates: location().coordinate, src: CoordinateSource.GEOCODE };
            case CvMarkerType.IMPORT:
              return {
                coordinates: location().coordinateImport,
                src: CoordinateSource.IMPORT,
              };
            case CvMarkerType.CUSTOM:
              return {
                coordinates: convertToCoordinateType(marker.position),
                src: CoordinateSource.CUSTOM,
              };
            default:
              return null;
          }
        };

        return {
          // Triggered when user click on marker button (validate/invalidate/revalidate)
          processMarkerAction: rxMethod<MapMarkerData>(pipe(
              switchMap((marker) => {
                const focusedLocation = store.selectSignal(selectFocusedLocation)();
                const action = determineMarkerAction(marker, focusedLocation.verified);
                const { coordinates, src } = action === CvMarkerAction.INVALIDATE
                    ? {}
                    : getPreciseCoordinates(marker) || {};

                return apiService.verifyCoordinates(focusedLocation, coordinates, src).pipe(
                    tapResponse({
                      next: (res) => {
                        const focused = store.selectSignal(selectFocusedLocation)();

                        if (!res?.result) return;

                        feedbackService.showSuccessSnackbar({
                          data: { userFriendlyMessage: `(ノ^_^)ノ Location has been successfully ${action.toLowerCase()}d.` },
                        });

                        store.dispatch(LocationsRefreshActions.patchLocation({ location: res.result }));

                        //*************** Focus next row and scroll it to view **********************/

                        if (signalStore.continueAfterValidation() &&
                            store.selectSignal(selectCanContinueAfterValidation)() &&
                            action !== CvMarkerAction.INVALIDATE) {

                          const currentRow = locationListApi.getRowById(String(focused?.id));
                          if (!currentRow) return;

                          const nextRow = locationListApi.getRowByIndex(currentRow.rowIndex + 1);
                          if (!nextRow) return;

                          locationListApi.scrollRowToView(nextRow);
                          store.dispatch(RowActions.updateFocusedRow({ id: Number(nextRow.id) }));
                        }
                      },
                      error: (err: HttpErrorResponse) => {
                        feedbackService.showAlertSnackbar({
                          data: {
                            userFriendlyMessage: `¯\\_(ツ)_/¯ There was an error while trying to ${action} the focused location.`,
                            detailedInfoFromBE: err?.error?.message ? err.error.message : err?.message,
                          },
                        }, err.status);
                        console.error(err);
                      },
                    }),
                );
              }),
          )),
        };
      }),
  );
}